'use client'

import { useEffect, useState } from 'react'
import {
    getOrganizations,
    deleteOrganization,
    Organization,
} from '@/lib/api/organization/organization-api'
import { getFileUrl } from '@/lib/utils/file-url'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdArrowForward,
    MdBusiness,
    MdDelete,
    MdEmail,
    MdGroups,
    MdPhone,
    MdStorefront,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'

const formatCnpj = (cnpj?: string | null): string => {
    if (!cnpj) return '-'
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
    )
}

export default function OrganizationsPage() {
    const navigate = useNavigate()
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)
    const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null)
    const [deleting, setDeleting] = useState(false)
    const limit = 18

    useEffect(() => {
        loadOrganizations()
    }, [page])

    const loadOrganizations = async () => {
        setLoading(true)
        try {
            const response = await getOrganizations(page, limit)
            if (response.success && response.result) {
                setOrganizations(response.result.data || [])
                setTotalPages(response.result.totalPages || 1)
                setTotal(response.result.total || 0)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar organizações',
                )
            }
        } catch {
            toast.error('Erro ao carregar organizações')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const response = await deleteOrganization(deleteTarget.id)
            if (response.success) {
                toast.success(
                    response.result?.message ||
                        'Organização removida com sucesso',
                )
                setDeleteTarget(null)
                loadOrganizations()
            } else {
                toast.error(response.error || 'Erro ao remover organização')
            }
        } catch {
            toast.error('Erro ao remover organização')
        } finally {
            setDeleting(false)
        }
    }

    if (loading && organizations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando organizações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdBusiness className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Organizações
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1
                                ? 'organização cadastrada'
                                : 'organizações cadastradas'}
                        </p>
                    </div>
                </div>
            </div>

            {organizations.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdBusiness className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhuma organização cadastrada
                    </h3>
                    <p className="text-base-content/60 max-w-md mx-auto">
                        As organizações serão exibidas aqui quando forem
                        criadas no sistema.
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                        {organizations.map(org => (
                            <div
                                key={org.id}
                                onClick={() =>
                                    navigate(`/organizations/${org.id}`)
                                }
                                className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                                <div className="relative p-6">
                                    {/* Header */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg flex-shrink-0 overflow-hidden">
                                            {org.logoUrl ? (
                                                <img
                                                    src={getFileUrl(org.logoUrl)}
                                                    alt={org.name}
                                                    className="w-14 h-14 object-contain"
                                                />
                                            ) : (
                                                <MdBusiness className="text-2xl text-white" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-base-content group-hover:text-secondary transition-colors line-clamp-1">
                                                {org.name}
                                            </h3>
                                            {org.tradingName && (
                                                <p className="text-sm text-base-content/50 line-clamp-1">
                                                    {org.tradingName}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                <span
                                                    className={`badge badge-sm ${
                                                        org.active
                                                            ? 'badge-success'
                                                            : 'badge-error'
                                                    } gap-1`}
                                                >
                                                    {org.active
                                                        ? 'Ativa'
                                                        : 'Inativa'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 flex items-center gap-1">
                                            <button
                                                type="button"
                                                title="Excluir organização"
                                                className="w-8 h-8 rounded-lg bg-base-200/50 hover:bg-error/10 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    setDeleteTarget(org)
                                                }}
                                            >
                                                <MdDelete className="text-base-content/40 hover:text-error transition-colors" />
                                            </button>
                                            <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                                <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="space-y-2 mb-4 text-sm text-base-content/60">
                                        {org.cnpj && (
                                            <p className="font-mono text-xs">
                                                CNPJ: {formatCnpj(org.cnpj)}
                                            </p>
                                        )}
                                        {org.email && (
                                            <div className="flex items-center gap-2">
                                                <MdEmail className="text-base flex-shrink-0" />
                                                <span className="truncate">
                                                    {org.email}
                                                </span>
                                            </div>
                                        )}
                                        {org.phone && (
                                            <div className="flex items-center gap-2">
                                                <MdPhone className="text-base flex-shrink-0" />
                                                <span>{org.phone}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Contadores */}
                                    <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-base-200/30 rounded-xl">
                                        <div className="flex items-center gap-2">
                                            <MdStorefront className="text-secondary" />
                                            <span className="text-sm font-semibold text-base-content">
                                                {org._count?.companies ?? 0}
                                            </span>
                                            <span className="text-xs text-base-content/50">
                                                {(org._count?.companies ??
                                                    0) === 1
                                                    ? 'empresa'
                                                    : 'empresas'}
                                            </span>
                                        </div>
                                        <div className="w-px h-4 bg-base-300" />
                                        <div className="flex items-center gap-2">
                                            <MdGroups className="text-secondary" />
                                            <span className="text-sm font-semibold text-base-content">
                                                {org._count?.userCompanies ??
                                                    0}
                                            </span>
                                            <span className="text-xs text-base-content/50">
                                                {(org._count?.userCompanies ??
                                                    0) === 1
                                                    ? 'usuário'
                                                    : 'usuários'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="pt-4 border-t border-base-300/50">
                                        <div className="text-xs text-base-content/50">
                                            Criada em{' '}
                                            {format(
                                                new Date(org.createdAt),
                                                'dd/MM/yyyy',
                                                { locale: ptBR },
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-3">
                            <button
                                className="btn btn-outline btn-sm gap-1"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                <MdArrowBack className="text-base" />
                                Anterior
                            </button>
                            <span className="text-sm text-base-content/60">
                                Página {page} de {totalPages}
                            </span>
                            <button
                                className="btn btn-outline btn-sm gap-1"
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Próxima
                                <MdArrowForward className="text-base" />
                            </button>
                        </div>
                    )}
                </>
            )}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Excluir Organização"
                message={`Tem certeza que deseja excluir a organização "${deleteTarget?.name}"? Todas as empresas vinculadas também serão desativadas. Esta ação não pode ser desfeita facilmente.`}
                loading={deleting}
                confirmButtonText="Excluir"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
